import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import HondaLoader from '../../components/common/HondaLoader';
import { sanitizeInput } from '../../utils/inputValidation';
import { getCesionesListing, getCessionInformation, resetCase } from '../../services/cesionesService';
import Swal from 'sweetalert2';

const ControlCesiones = ({ user, onLogout, onNavigate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalConfig, setModalConfig] = useState({ isOpen: false, row: null });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const formatearFecha = (fecha) => {
    if (!fecha || fecha === 'N/A') return 'N/A';
    try {
      if (fecha.includes('/')) { const [d, m, y] = fecha.split('/'); return `${d}-${m}-${y}`; }
      if (fecha.includes('-')) { const p = fecha.split('-'); if (p[0].length === 4) { return `${p[2]}-${p[1]}-${p[0]}`; } return fecha; }
      return fecha;
    } catch { return fecha; }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const rechazados = await getCesionesListing('RECHAZADO', user.ce_ID);
        if (rechazados.length === 0) { setData([]); return; }
        const mapped = rechazados.map((item, idx) => ({
          id: item.cessionID || idx + 1,
          cessionID: item.cessionID || '',
          grupo: item.objContract?.group || 'N/A',
          orden: item.objContract?.order || 'N/A',
          comprador: item.objBuyer?.fullName || 'N/A',
          vendedor: item.objSeller?.fullName || 'N/A',
          fechaEstado: formatearFecha(item.statusDate || ''),
          estado: item.cessionStatus || 'RECHAZADO',
        }));
        setData(mapped);
      } catch (err) {
        console.error('Error cargando cesiones rechazadas:', err);
        setError(err.message || 'Error al cargar los datos');
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const filteredData = data.filter(row => {
    const s = searchTerm.toLowerCase();
    return (
      String(row.grupo).toLowerCase().includes(s) ||
      String(row.orden).toLowerCase().includes(s) ||
      String(row.comprador).toLowerCase().includes(s) ||
      String(row.cessionID).toLowerCase().includes(s)
    );
  });

  const handleVerDetalle = useCallback((row) => {
    onNavigate('visualizacionCesion', {
      cessionID: row.cessionID,
      grupo: row.grupo !== 'N/A' ? row.grupo : '',
      orden: row.orden !== 'N/A' ? row.orden : '',
      estado: row.estado,
      soloVisualizacion: true,
    });
  }, [onNavigate]);

  const handleRecargarDoc = useCallback(async (row) => {
    try {
      setLoading(true);
      const cessionData = await getCessionInformation({
        cessionID: row.cessionID,
        group: row.grupo !== 'N/A' ? row.grupo : '',
        order: row.orden !== 'N/A' ? row.orden : '',
      });
      onNavigate('iniciarCesion', {
        cessionID: row.cessionID,
        cessionData,
      });
    } catch (err) {
      console.error('Error al obtener información de la cesión:', err);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message || 'No se pudo obtener la información de la cesión.',
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setLoading(false);
    }
  }, [onNavigate]);

  const handleDeshacerClick = useCallback((row) => {
    setModalConfig({ isOpen: true, row });
  }, []);

  const handleConfirmarDeshacer = useCallback(async () => {
    const { row } = modalConfig;
    setModalConfig({ isOpen: false, row: null });
    try {
      setLoading(true);
      await resetCase({
        group: row.grupo !== 'N/A' ? row.grupo : '',
        order: row.orden !== 'N/A' ? row.orden : '',
        cessionID: row.cessionID,
      });
      await Swal.fire({
        icon: 'success',
        title: 'Caso reseteado',
        text: 'El caso fue reseteado exitosamente.',
        confirmButtonColor: '#ef4444',
      });
      // Recargar la lista
      const rechazados = await getCesionesListing('RECHAZADO', user.ce_ID);
      const mapped = rechazados.map((item, idx) => ({
        id: item.cessionID || idx + 1,
        cessionID: item.cessionID || '',
        grupo: item.objContract?.group || 'N/A',
        orden: item.objContract?.order || 'N/A',
        comprador: item.objBuyer?.fullName || 'N/A',
        vendedor: item.objSeller?.fullName || 'N/A',
        fechaEstado: formatearFecha(item.statusDate || ''),
        estado: item.cessionStatus || 'RECHAZADO',
      }));
      setData(mapped);
    } catch (err) {
      console.error('Error al resetear el caso:', err);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message || 'No se pudo resetear el caso.',
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setLoading(false);
    }
  }, [modalConfig, formatearFecha]);

  const handleCancelarModal = useCallback(() => {
    setModalConfig({ isOpen: false, row: null });
  }, []);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.dropdown-container')) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  return (
    <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
      <div className="max-w-full mx-auto px-3 py-4">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1.5 h-8 gradient-red rounded-full shadow-glow-red"></div>
            <h2 className="text-2xl font-bold text-gradient">Control de Cesiones</h2>
          </div>
          <p className="text-gray-500 text-sm ml-5">Gestione las cesiones rechazadas</p>
        </div>

        {/* Buscador */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Buscar</label>
              <div className="relative">
                <span className="material-icons absolute left-3 top-2.5 text-gray-400 text-sm">search</span>
                <input type="text" placeholder="Buscar por grupo, orden, comprador o Cession ID..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(sanitizeInput(e.target.value))}
                  className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
                />
              </div>
            </div>
            <div className="flex items-end">
              <div className="relative dropdown-container">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center justify-between gap-2 px-3 xl:px-4 py-2 bg-white border border-gray-200 rounded-md text-xs xl:text-sm text-gray-700 hover:bg-gray-50 transition min-w-[160px]"
                >
                  <span className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    Rechazados
                  </span>
                  <span className="material-icons text-gray-400 text-base">
                    {isDropdownOpen ? 'expand_less' : 'expand_more'}
                  </span>
                </button>
                {isDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg z-10 overflow-hidden">
                    <button
                      onClick={() => setIsDropdownOpen(false)}
                      className="w-full text-left px-4 py-2 text-sm bg-red-50 text-red-600 transition"
                    >
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-500"></span>
                        Rechazados
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && !loading && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 mb-6 flex items-center gap-3">
            <span className="material-icons text-red-600">error_outline</span>
            <div>
              <p className="text-sm font-bold text-red-800">Error al cargar datos</p>
              <p className="text-xs text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Tabla */}
        {loading ? (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12">
            <HondaLoader message="Cargando cesiones rechazadas..." variant="inline" size="large" />
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-red-600 to-red-700">
                  <tr>
                    {['Cession ID','Grupo','Orden','Comprador','Vendedor','Estado'].map(h => (
                      <th key={h} className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{h}</th>
                    ))}
                    <th className="px-4 py-4 text-center text-xs font-bold text-white uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                        <span className="material-icons text-4xl text-gray-300 block mb-2">assignment_turned_in</span>
                        {searchTerm ? 'No se encontraron resultados para la búsqueda' : 'No hay cesiones rechazadas en este momento'}
                      </td>
                    </tr>
                  ) : filteredData.map(row => (
                    <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono">{row.cessionID}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{row.grupo}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{row.orden}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{row.comprador}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{row.vendedor}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{row.estado}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleVerDetalle(row); }}
                            className="text-blue-600 hover:bg-blue-600 hover:text-white transition-colors p-1 rounded-full"
                            title="Ver detalle"
                          >
                            <span className="material-icons text-xl">visibility</span>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeshacerClick(row); }}
                            className="text-red-600 hover:bg-red-600 hover:text-white transition-colors p-1 rounded-full"
                            title="Deshacer cesion"
                          >
                            <span className="material-icons text-xl">undo</span>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRecargarDoc(row); }}
                            className="text-green-600 hover:bg-green-600 hover:text-white transition-colors p-1 rounded-full"
                            title="Recargar documentacion"
                          >
                            <span className="material-icons text-xl">arrow_forward</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredData.length > 0 && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
                Mostrando {filteredData.length} de {data.length} cesiones rechazadas
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Deshacer */}
      {modalConfig.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-red-600 to-red-700">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white/20">
                  <span className="material-icons text-2xl text-white">undo</span>
                </div>
                <h3 className="text-lg font-semibold text-white">Deshacer Cesion</h3>
              </div>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-700 mb-4">¿Está seguro que desea deshacer esta cesion?</p>
              {modalConfig.row && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">Cession ID:</span>
                    <span className="text-sm text-gray-800 font-mono">{modalConfig.row.cessionID}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">Comprador:</span>
                    <span className="text-sm text-gray-800 font-semibold">{modalConfig.row.comprador}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">Grupo:</span>
                    <span className="text-sm text-gray-800">{modalConfig.row.grupo}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">Orden:</span>
                    <span className="text-sm text-gray-800">{modalConfig.row.orden}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={handleCancelarModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmarDeshacer}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2"
              >
                <span className="material-icons text-sm">undo</span>
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ControlCesiones;